import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
const weekdays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"];
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (value) => (value ? new Date(`${value}T12:00:00`) : new Date());
export default function DatePicker({
  id,
  label,
  value,
  onChange,
  minDate,
  maxDate,
  disabled = false,
}) {
  const [open, setOpen] = useState(false),
    [month, setMonth] = useState(parse(value)),
    [position, setPosition] = useState({ left: 12, top: 12 }),
    ref = useRef(null),
    popover = useRef(null);
  useEffect(() => {
    const outside = (e) => {
      if (
        !ref.current?.contains(e.target) &&
        !popover.current?.contains(e.target)
      )
        setOpen(false);
    };
    const escape = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, []);
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const r = ref.current.getBoundingClientRect(),
        height = popover.current?.offsetHeight || 320,
        width = Math.min(290, innerWidth - 24),
        left = Math.max(12, Math.min(r.left, innerWidth - width - 12));
      setPosition({
        left,
        top:
          r.bottom + height + 20 > innerHeight
            ? Math.max(12, r.top - height - 8)
            : r.bottom + 8,
        width,
      });
    };
    update();
    addEventListener("resize", update);
    addEventListener("scroll", update, true);
    return () => {
      removeEventListener("resize", update);
      removeEventListener("scroll", update, true);
    };
  }, [open, month]);
  const first = new Date(month.getFullYear(), month.getMonth(), 1).getDay(),
    days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  return (
    <div className="date-field" ref={ref}>
      <label htmlFor={id}>{label}</label>
      <button
        id={id}
        className="date-input"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setMonth(parse(value));
          setOpen(!open);
        }}
      >
        <span>
          {value ? value.split("-").reverse().join("/") : "Selecione uma data"}
        </span>
        <CalendarDays size={17} />
      </button>
      {open &&
        createPortal(
          <div
            ref={popover}
            className="calendar"
            style={{ position: "fixed", ...position }}
            role="dialog"
            aria-label={`Calendário ${label}`}
          >
            <div className="calendar-head">
              <button
                aria-label="Mês anterior"
                onClick={() =>
                  setMonth(
                    new Date(month.getFullYear(), month.getMonth() - 1, 1),
                  )
                }
              >
                <ChevronLeft size={18} />
              </button>
              <strong>
                {month.toLocaleDateString("pt-BR", {
                  month: "long",
                  year: "numeric",
                })}
              </strong>
              <button
                aria-label="Próximo mês"
                onClick={() =>
                  setMonth(
                    new Date(month.getFullYear(), month.getMonth() + 1, 1),
                  )
                }
              >
                <ChevronRight size={18} />
              </button>
            </div>
            <div className="calendar-grid">
              {weekdays.map((d) => (
                <span key={d}>{d}</span>
              ))}
              {Array.from({ length: first }, (_, i) => (
                <i key={`blank${i}`} />
              ))}
              {Array.from({ length: days }, (_, i) => {
                const day = iso(
                  new Date(month.getFullYear(), month.getMonth(), i + 1),
                );
                return (
                  <button
                    key={day}
                    disabled={
                      (minDate && day < minDate) || (maxDate && day > maxDate)
                    }
                    className={day === value ? "selected" : ""}
                    aria-label={day.split("-").reverse().join("/")}
                    onClick={() => {
                      onChange(day);
                      setOpen(false);
                    }}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
